import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    ViewChild,
} from "@angular/core";
import {MatSlideToggleChange} from "@angular/material/slide-toggle";
import {Subscription} from "rxjs";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MatSort} from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {ViewerErrorCommunicatorService} from "../../services/communicators/viewer-error-communicator.service";
import {LayerService} from "../../services/internal-services/layer.service";
import {ViewerConstants} from "../../../shared/model/viewer-constants";
import {GeoService} from "../../services/internal-services/geo.service";
import {IMetadataImage} from "../../interfaces/i-metadataimage";
import {MapStatus} from "../../model/map-status";
import {MapService} from "../../services/internal-services/map.service";
import {toolbarOptions} from "../../../shared/model/toolbar-constants";
import {ToolbarService} from "../../services/internal-services/toolbar.service";
import {EncodingService} from "../../services/internal-services/encoding.service";
import {ActivatedRoute} from "@angular/router";

import * as FormatUtils from "@vision/core-map-api/src/utils/FormatUtils";
import {VIEWER_SERVICE} from "../../services/internal-services/viewer-services/inject-base-viewer.service";

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "app-developer-tools",
    templateUrl: "./developer-tools.component.html",
    styleUrls: ["./developer-tools.component.scss"],

    standalone: false,
})
export class DeveloperToolsComponent implements OnInit, AfterViewInit {
    viewerService = inject(VIEWER_SERVICE);

    enabled = false;

    showDebugPanel = false;

    showGridControl = false;

    showLayerControl = false;

    showCoverageControl = false;

    showLocationPanel = false;

    showImagesPanel = false;

    showPaintPanel = false;

    showGeoTiffPanel = false;

    selectedGeoTiffFileName = "";

    embeddingsImplementation: string = "vector";

    showStatePanel = false;

    showAois = false;

    showViewPanel = false;

    showFootprintsPanel = false;

    showDSMToolsInfo = false;

    showCoveragePanel = false;

    enableFeaturesPopup = false;

    enableImageDiscreteInfo = false;

    paintFootprints = false;

    reprojecInObliques = false;

    showPolygonTriangulation = false;

    lockDiscrete = false;

    downloadImageCoverage = false;

    showDSMStatusPanel = false;

    DSMCoverage = false;

    lockDSMDownloadStatus = false;

    showStatePlain: boolean[] = [false, false, false];

    dsmTileZoom = 0;

    footprintsOrientation = "";

    subscriptions: Subscription[] = [];

    centerForm: FormGroup;

    tileForm: FormGroup;

    wktForm: FormGroup;

    geojsonForm: FormGroup;

    zoomForm: FormGroup;

    mapStatus: MapStatus;

    coreMap: any;

    @ViewChild(MatPaginator, {static: true})
    paginator: MatPaginator;

    @ViewChild(MatSort, {static: true}) sort: MatSort;

    imagesDataSource: MatTableDataSource<IMetadataImage>;

    expandedElement: IMetadataImage | null;

    toolbarOption = toolbarOptions.DEVELOPER_PANEL;

    displayPanel = false;

    enableMask: boolean;

    checkBorders: boolean;

    checkCoverage: boolean;

    states: {'plain': string, 'decoded': any}[] = [];

    centerWkt: string;

    extentWkt: string;

    dsmInfo: any;

    protected readonly Object = Object;

    constructor(
        private mapService: MapService,
        private fb: FormBuilder,
        private errorCommunicator: ViewerErrorCommunicatorService,
        private layerService: LayerService,
        private toolbarService: ToolbarService,
        protected route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
    ) {
        this.centerForm = this.fb.group({
            coordinates: ["", [Validators.required]],
        });
        this.tileForm = this.fb.group({
            tile: ["", [Validators.required]],
        });
        this.zoomForm = this.fb.group({
            zoom: ["", [Validators.required]],
        });

        this.wktForm = this.fb.group({
            wkt: ["", [Validators.required]],
            epsg: ["", [Validators.required]],
        });

        this.geojsonForm = this.fb.group({
            geojson: ["", [Validators.required]],
            epsg: ["", [Validators.required]],
        });

        this.imagesDataSource = new MatTableDataSource();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.viewerService.developerTools$.subscribe((enable: boolean) => {
                this.enabled = enable;
            }),
        );

        this.subscriptions.push(
            this.viewerService.loadedImages$.subscribe((images: IMetadataImage[]) => {
                // only expand if we have the detail panel closed
                if (!this.expandedElement) {
                    this.imagesDataSource.data = images;
                }
            }),
        );

        this.subscriptions.push(
            this.mapService.mapStatus$.subscribe((mapStatus: MapStatus) => {
                if (mapStatus) {
                    this.mapStatus = mapStatus;
                    this.centerWkt = FormatUtils.transformWkt(GeoService.geoJSONFeatureToWKT(mapStatus.center), {
                        dataProjection: ViewerConstants.PROJECTIONS.WebMercator,
                        featureProjection: ViewerConstants.PROJECTIONS.WGS84,
                    });
                    this.extentWkt = FormatUtils.transformWkt(GeoService.geoJSONFeatureToWKT(mapStatus.extent), {
                        dataProjection: ViewerConstants.PROJECTIONS.WebMercator,
                        featureProjection: ViewerConstants.PROJECTIONS.WGS84,
                    });
                }
            }),
        );
        this.subscriptions.push(
            this.mapService.coreMap$.subscribe((coreMap: any) => {
                if (coreMap) {
                    this.coreMap = coreMap;
                    this.enableMask = !this.coreMap.map.get("showMaskedTiles");
                    this.checkBorders = !this.coreMap.map.get("showBorderTiles");
                    this.checkCoverage = this.coreMap.map.get("checkOuterTiles");
                }
            }),
        );
        this.subscriptions.push(
            this.toolbarService.enabledTool$.subscribe((enabledTool) => {
                this.displayPanel = this.toolbarOption === enabledTool;
            }),
        );
        this.subscriptions.push(
            this.route.queryParams.subscribe((params) => {
                //IA Studio
                if (params.state0) {
                    this.states[0] = {
                        'plain': params.state0,
                        'decoded': JSON.parse(EncodingService.decodingB64(params.state0))
                    };
                }
                //Viewer
                if (params.state1) {
                    this.states[1] = {
                        'plain': params.state1,
                        'decoded': JSON.parse(EncodingService.decodingB64(params.state1))
                    };
                }
                if (params.state2) {
                    this.states[2] = {
                        'plain': params.state2,
                        'decoded': JSON.parse(EncodingService.decodingB64(params.state2))
                    };
                }
                this.cdr.detectChanges();
            }),
        );
    }

    ngAfterViewInit() {
        if (this.mapService.isMapInitialized()) {
            const debugParameters = this.mapService.getCoreMap().getDebugParameters();
            this.showGridControl = debugParameters.paintTileGrid;
            this.showDebugPanel = debugParameters.showStatusControl;
            this.showLayerControl = debugParameters.showLayerControl;
            this.showCoverageControl = debugParameters.showCoverageControl;
            this.enableFeaturesPopup = debugParameters.enableFeaturesPopup;
            this.paintFootprints = debugParameters.paintFootprints;
        }
        this.imagesDataSource.paginator = this.paginator;
        this.imagesDataSource.sort = this.sort;
    }

    togglePanelDisplay() {
        this.toolbarService.enableTool(!this.displayPanel ? this.toolbarOption : toolbarOptions.NONE);
    }

    changeEnableMask(event: MatSlideToggleChange) {
        this.enableMask = event.checked;
        this.coreMap.map.set("showMaskedTiles", !this.enableMask);
    }

    changeCheckBorders(event: MatSlideToggleChange) {
        this.checkBorders = event.checked;
        this.coreMap.map.set("showBorderTiles", !this.checkBorders);
    }

    changeCheckCoverage(event: MatSlideToggleChange) {
        this.checkCoverage = event.checked;
        this.coreMap.map.set("checkOuterTiles", !this.checkCoverage);
    }

    changeDebugPanelStatus(event: MatSlideToggleChange) {
        this.showDebugPanel = event.checked;
        this.viewerService.enableDebugPanel(event.checked);
    }

    changeViewPanelStatus(event: MatSlideToggleChange) {
        this.showViewPanel = event.checked;
    }

    changeReprojectInObliques(event: MatSlideToggleChange) {
        this.reprojecInObliques = event.checked;
        this.viewerService.reprojectObliques(event.checked);
    }

    changeShowPolygonTriangulation(event: MatSlideToggleChange) {
        this.showPolygonTriangulation = event.checked;
        this.viewerService.showPolygonTriangulation(event.checked);
    }

    lockDiscreteView(event: MatSlideToggleChange) {
        this.lockDiscrete = event.checked;
        this.mapService.lockDiscreteView(event.checked);
    }

    changePaintPanelStatus(event: MatSlideToggleChange) {
        this.showPaintPanel = event.checked;
    }

    changeGeoTiffPanelStatus(event: MatSlideToggleChange) {
        this.showGeoTiffPanel = event.checked;
    }

    /**
     * Loads a locally-picked GeoTIFF file as a map layer, via an object URL — the same
     * addGeoTiffLayer path used by the AI chat's "Add layer" action, for quick manual testing.
     */
    onGeoTiffFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        const url = URL.createObjectURL(file);
        const name = file.name.replace(/\.[^.]+$/, "");
        void this.viewerService.addGeoTiffLayer(url, name);
        this.selectedGeoTiffFileName = file.name;
        input.value = ""; // allow re-selecting the same file
    }

    onChangeEmbeddingsImplementation(event: Event) {
        this.embeddingsImplementation = (event.target as HTMLInputElement).value;
        this.viewerService.setEmbeddingImplementation(this.embeddingsImplementation);
    }

    changeStatePanel(event: MatSlideToggleChange) {
        this.showStatePanel = event.checked;
    }

    changeShowAois(event: MatSlideToggleChange) {
        this.showAois = event.checked;
    }

    changeShowDSMTools(event: MatSlideToggleChange) {
        this.showDSMToolsInfo = event.checked;
        if (event.checked) {
            this.dsmTileZoom = this.mapService.getDsmTileZoom();
        }
    }

    changeLocationPanelStatus(event: MatSlideToggleChange) {
        this.showLocationPanel = event.checked;
    }

    changeGridControlStatus(event: MatSlideToggleChange) {
        this.showGridControl = event.checked;
        this.viewerService.enableDebugPaintGrid(event.checked);
    }

    changeLayerControlStatus(event: MatSlideToggleChange) {
        this.showLayerControl = event.checked;
        this.viewerService.enableDebugLayerControl(event.checked);
    }

    changeCoverageControlStatus(event: MatSlideToggleChange) {
        this.showCoveragePanel = event.checked;
    }

    changeFootprintControlStatus(event: MatSlideToggleChange) {
        this.showFootprintsPanel = event.checked;
    }

    changeImagesControlStatus(event: MatSlideToggleChange) {
        this.showImagesPanel = event.checked;
    }

    changeEnableImageInfo(event: MatSlideToggleChange) {
        this.enableImageDiscreteInfo = event.checked;
        this.viewerService.enableImageInfo(event.checked);
    }

    centerOnCoordinates() {
        const inputValue = this.centerForm.get("coordinates").value;
        const latlng = inputValue.trim().split(" ");
        if (latlng.length === 2) {
            this.mapService.setCenter(latlng, "EPSG:4326");
        } else {
            this.errorCommunicator.setViewerError("This coordinates are not valid, try lat long separated by space on WGS84");
        }
    }

    wktToDrawLayer() {
        const wktValue = this.wktForm.get("wkt").value;
        const epsgValue = this.wktForm.get("epsg").value;

        if (wktValue && epsgValue) {
            this.paintWKT(wktValue, epsgValue, "Debug feature");
        }
    }

    geojsonToDrawLayer() {
        const geoJSONValue = this.geojsonForm.get("geojson").value;
        const epsgValue = this.geojsonForm.get("epsg").value;
        if (geoJSONValue && epsgValue) {
            this.paintGeoJSON(JSON.parse(geoJSONValue), epsgValue);
        }
    }

    paintWKT(wktValue, epsgValue = ViewerConstants.PROJECTIONS.WGS84, title, fit = true) {
        const wkt = wktValue.trim().replace(/['"]+/g, ""); // trim and remove quotes
        const epsg = epsgValue.trim().replace(/epsg:/gi, "");
        const wktFeature = this.layerService.createFeatureWkt(this.mapService.getCoreMap(), wkt, `EPSG:${epsg}`, {title});
        this.mapService.getDrawingLayer().getSource().addFeature(wktFeature);
        // wkt feature its returned on mercator
        if (fit) {
            this.mapService.fitToExtent(wktFeature.getGeometry().getExtent(), ViewerConstants.PROJECTIONS.WebMercator);
        }
    }

    fit(extent: number[], epsg = ViewerConstants.PROJECTIONS.WebMercator) {
        this.mapService.fitToExtent(extent, epsg);
    }

    paintGeoJSON(geojson, epsg = ViewerConstants.PROJECTIONS.WebMercator, title = null) {
        const wkt = GeoService.geoJSONFeatureToWKT(geojson);
        const code = epsg.trim().replace(/epsg:/gi, "");
        const wktFeature = this.layerService.createFeatureWkt(this.mapService.getCoreMap(), wkt, `EPSG:${code}`, {title});
        this.mapService.getDrawingLayer().getSource().addFeature(wktFeature);
    }

    showMapStatus(): boolean {
        return !!this.coreMap;
    }

    findTile() {
        const inputValue = this.tileForm.get("tile").value;
        const tileParameters = inputValue.trim().split("/");
        if (tileParameters.length !== 3) {
            this.errorCommunicator.setViewerError("This tile coordinates are not valid");
        } else {
            const latlng = GeoService.tileToLatLng(tileParameters[1], tileParameters[2], tileParameters[0]);
            this.mapService.setCenter([latlng[1], latlng[0]], ViewerConstants.PROJECTIONS.WGS84);
            this.mapService.getCoreMap().setZoom(tileParameters[0]);
        }
    }

    changeZoom() {
        const inputValue = this.zoomForm.get("zoom").value;
        this.viewerService.setZoom(inputValue);
    }

    changeShowFootprints(event: MatSlideToggleChange) {
        this.paintFootprints = event.checked;
        this.viewerService.enableFootprints(event.checked, this.footprintsOrientation);
    }

    changeFootprintsOrientation() {
        this.viewerService.enableFootprints(this.paintFootprints, this.footprintsOrientation);
    }

    closeAoiPanel() {
        this.showAois = false;
    }

    closeImagesPanel() {
        this.showImagesPanel = false;
    }

    changeDSMTileZoom(tileZoom) {
        this.mapService.changeDsmTileZoom(tileZoom);
    }

    changeDSMDownloadCoverage(event: MatSlideToggleChange) {
        this.downloadImageCoverage = event.checked;
        this.mapService.enableDSMImageCoverage(event.checked);
    }

    showDSMTilesStatus(event: MatSlideToggleChange) {
        this.showDSMStatusPanel = event.checked;
        this.mapService.enableDSMTileStatus(event.checked);
    }

    showDSMCoverage(event: MatSlideToggleChange) {
        this.DSMCoverage = event.checked;
        this.mapService.showDSMCoverage(event.checked);
    }

    lockDSMDownload(event: MatSlideToggleChange) {
        this.lockDSMDownloadStatus = event.checked;
        this.mapService.lockDSMDownload(event.checked);
    }

    getDSMCachedInfo() {
        this.dsmInfo = this.mapService.getDSMCachedInfo();
    }

    cleanDSMTilesCache() {
        this.mapService.cleanDSMTilesCache();
    }

    toggleStatePlain(index: number): void {
        this.showStatePlain[index] = !this.showStatePlain[index];
    }


}
